import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Input,
  IconButton,
  FormHelperText,
  Paper,
  InputAdornment,
  Grid,
  Chip,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import { API_ENDPOINTS } from "../../../config/api";

const API_URL = API_ENDPOINTS.APP_USERS;

const EditAppUserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appUser, setAppUser] = useState({
    user_type_id: "",
    region_id: [],
    company_name: "",
    contact_person: "",
    profile_pic: null,
    mobile: "",
    alternate_number: "",
    email: "",
    street_line: "",
    town_city: "",
    state: "",
    country: "",
    pincode: "",
    gstin: "",
    pan: "",
  });
  const [currentProfilePicUrl, setCurrentProfilePicUrl] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userTypes, setUserTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [appUserResponse, userTypesResponse, regionsResponse] = await Promise.all([
          axios.get(API_URL.EDIT(id)),
          axios.get(API_ENDPOINTS.USER_TYPE_NAMES),
          axios.get(API_ENDPOINTS.REGION_NAMES),
        ]);

        const appUserData = appUserResponse.data.appUser || {};
        const initialRegionIds = appUserData.region_id
          ? String(appUserData.region_id).split(',').map(id => id.trim()).filter(id => id)
          : [];

        setAppUser({
          user_type_id: appUserData.user_type_id?.toString() || "",
          region_id: initialRegionIds,
          company_name: appUserData.company_name || "",
          contact_person: appUserData.contact_person || "",
          profile_pic: null,
          mobile: appUserData.mobile || "",
          alternate_number: appUserData.alternate_number || "",
          email: appUserData.email || "",
          street_line: appUserData.street_line || "",
          town_city: appUserData.town_city || "",
          state: appUserData.state || "",
          country: appUserData.country || "",
          pincode: appUserData.pincode || "",
          gstin: appUserData.gstin || "",
          pan: appUserData.pan || "",
        });
        setCurrentProfilePicUrl(appUserData.profile_pic || null);
        setPreview(appUserData.profile_pic || null);

        setUserTypes(
          Array.isArray(userTypesResponse.data)
            ? userTypesResponse.data
            : userTypesResponse.data.data || Object.values(userTypesResponse.data)
        );
        setRegions(
          Array.isArray(regionsResponse.data)
            ? regionsResponse.data
            : regionsResponse.data.data || Object.values(regionsResponse.data)
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: `Failed to load data: ${error.message}`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        setAppUser((prev) => ({ ...prev, profile_pic: null }));
        setPreview(currentProfilePicUrl || null);
        setFileInputKey(Date.now());
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        setAppUser((prev) => ({ ...prev, profile_pic: null }));
        setPreview(currentProfilePicUrl || null);
        setFileInputKey(Date.now());
        return;
      }
      setError("");
      setAppUser((prev) => ({ ...prev, profile_pic: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Filter out non-digit characters for mobile and alternate_number fields
    let processedValue = value;
    if (name === "mobile" || name === "alternate_number") {
      processedValue = value.replace(/\D/g, '').slice(0, 10); // Only allow digits and limit to 10
    } else if (name === "gstin") {
      // Allow alphanumeric characters for GSTIN and limit to 15
      processedValue = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 15).toUpperCase();
    } else if (name === "pan") {
      // Allow alphanumeric characters for PAN and limit to 10
      processedValue = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 10).toUpperCase();
    } else if (name === "pincode") {
      // Only allow digits and limit to 6 for pincode
      processedValue = value.replace(/\D/g, '').slice(0, 6);
    }

    if (name === 'region_id') {
      setAppUser((prev) => ({
        ...prev,
        [name]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setAppUser((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
    }
  };

  const handleRemoveFile = () => {
    setAppUser((prev) => ({ ...prev, profile_pic: null }));
    setPreview(currentProfilePicUrl || null);
    setError("");
    setFileInputKey(Date.now());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Phone, email, GSTIN, and PAN validation
    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const gstinRegex = /^[A-Za-z0-9]{15}$/;
    const panRegex = /^[A-Za-z0-9]{10}$/;

    if (!appUser.user_type_id) {
      setSnackbar({ open: true, message: "User Type is required", severity: "error" });
      return;
    }
    if (appUser.region_id.length === 0) {
      setSnackbar({ open: true, message: "Region is required", severity: "error" });
      return;
    }
    if (!appUser.company_name.trim()) {
      setSnackbar({ open: true, message: "Company Name is required", severity: "error" });
      return;
    }
    if (!appUser.contact_person.trim()) {
      setSnackbar({ open: true, message: "Contact Person is required", severity: "error" });
      return;
    }
    if (!appUser.mobile.trim()) {
      setSnackbar({ open: true, message: "Mobile is required", severity: "error" });
      return;
    }
    if (!phoneRegex.test(appUser.mobile)) {
      setSnackbar({ open: true, message: "Mobile number must be 10 digits", severity: "error" });
      return;
    }
    if (appUser.alternate_number && !phoneRegex.test(appUser.alternate_number)) {
      setSnackbar({ open: true, message: "Alternate number must be 10 digits", severity: "error" });
      return;
    }
    if (!appUser.email.trim()) {
      setSnackbar({ open: true, message: "Email is required", severity: "error" });
      return;
    }
    if (!emailRegex.test(appUser.email.trim())) {
      setSnackbar({ open: true, message: "Invalid email address", severity: "error" });
      return;
    }
    if (appUser.gstin && !gstinRegex.test(appUser.gstin)) {
      setSnackbar({ open: true, message: "GSTIN must be 15 alphanumeric characters", severity: "error" });
      return;
    }
    if (appUser.pan && !panRegex.test(appUser.pan)) {
      setSnackbar({ open: true, message: "PAN must be 10 alphanumeric characters", severity: "error" });
      return;
    }
    if (appUser.pincode && !/^\d{6}$/.test(appUser.pincode)) {
      setSnackbar({ open: true, message: "Pincode must be exactly 6 digits", severity: "error" });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("user_type_id", appUser.user_type_id);
      formData.append("region_id", appUser.region_id.join(','));
      formData.append("company_name", appUser.company_name);
      formData.append("contact_person", appUser.contact_person);
      formData.append("mobile", appUser.mobile);
      formData.append("alternate_number", appUser.alternate_number);
      formData.append("email", appUser.email);
      formData.append("street_line", appUser.street_line);
      formData.append("town_city", appUser.town_city);
      formData.append("state", appUser.state);
      formData.append("country", appUser.country);
      formData.append("pincode", appUser.pincode);
      formData.append("gstin", appUser.gstin);
      formData.append("pan", appUser.pan);
      if (appUser.profile_pic instanceof File) {
        formData.append("profile_pic", appUser.profile_pic);
      }
      formData.append("_method", "PUT");

      await axios.post(API_URL.EDIT(id), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbar({
        open: true,
        message: "App User updated successfully!",
        severity: "success",
      });
      setTimeout(() => navigate("/app-users"), 1500);
    } catch (error) {
      console.error("Error updating app user:", error);
      const userMessage =
        error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(", ")
          : error.response?.data?.message || "Failed to update app user.";
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false, message: "", severity: "success" }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 5, flexDirection: "column", gap: 2 }}>
        <CircularProgress />
        <Typography>Loading users data...</Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ p: 4, width: "100%", mx: "auto", mt: 5, boxShadow: 3 }}>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth>
              <div className="image-field">
                <Input
                  id="profile_pic"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  key={fileInputKey}
                  sx={{ display: 'none' }}
                />
                <label htmlFor="profile_pic">
                  <Box
                    sx={{
                      border: '2px dashed #B0C4DE',
                      borderRadius: '10px',
                      padding: '20px',
                      textAlign: 'center',
                      backgroundColor: '#F0F8FF',
                      cursor: 'pointer',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    {preview ? (
                      <>
                        <img
                          src={preview}
                          alt="Uploaded"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '80px',
                            marginBottom: '10px',
                            objectFit: 'contain',
                          }}
                        />
                        <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                          {appUser.profile_pic instanceof File ? appUser.profile_pic.name : "Current Image"}
                        </Typography>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            handleRemoveFile();
                          }}
                          sx={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <UploadIcon
                          sx={{ fontSize: '40px', color: '#4682B4', marginBottom: '10px' }}
                        />
                        <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                          Upload Profile Picture
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#4682B4' }}>
                          Supports: JPG, JPEG, PNG
                        </Typography>
                      </>
                    )}
                  </Box>
                </label>
              </div>
              {error && <FormHelperText error>{error}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required variant="outlined">
              <InputLabel>User Type</InputLabel>
              <Select
                name="user_type_id"
                value={appUser.user_type_id}
                onChange={handleChange}
                label="User Type"
              >
                <MenuItem value="">Select User Type</MenuItem>
                {userTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id.toString()}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Company Name"
                name="company_name"
                value={appUser.company_name}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Contact Person Name"
                name="contact_person"
                value={appUser.contact_person}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
              />
            </FormControl>
          </Grid>
        
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Mobile"
                name="mobile"
                value={appUser.mobile}
                onChange={handleChange}
                required
                variant="outlined"
                inputProps={{ maxLength: 10 }}
                helperText="Note: Enter 10 digit mobile number"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Alternate Number"
                name="alternate_number"
                value={appUser.alternate_number}
                onChange={handleChange}
                variant="outlined"
                inputProps={{ maxLength: 10 }}
                helperText="Note: Enter 10 digit alternate number (optional)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Email"
                name="email"
                value={appUser.email}
                onChange={handleChange}
                required
                fullWidth
                type="email"
                variant="outlined"
              />
            </FormControl>
          </Grid>
         
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Country"
                name="country"
                value={appUser.country}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="State"
                name="state"
                value={appUser.state}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="City"
                name="town_city"
                value={appUser.town_city}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Street Line"
                name="street_line"
                value={appUser.street_line}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </FormControl>
          </Grid>
        
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="Pincode"
                name="pincode"
                value={appUser.pincode}
                onChange={handleChange}
                inputProps={{ 
                  maxLength: 6,
                  pattern: "[0-9]*",
                  inputMode: "numeric"
                }}
                helperText="Note: Enter 6 digit pincode (optional)"
                variant="outlined"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="GSTIN"
                name="gstin"
                value={appUser.gstin}
                onChange={handleChange}
                variant="outlined"
                inputProps={{ maxLength: 15 }}
                helperText="Note: Enter 15 alphanumeric characters (optional)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                label="PAN Number"
                name="pan"
                value={appUser.pan}
                onChange={handleChange}
                variant="outlined"
                inputProps={{ maxLength: 10 }}
                helperText="Note: Enter 10 alphanumeric characters (optional)"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              Region
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {regions.map((region) => (
                <Chip
                  key={region.id}
                  label={region.name}
                  onClick={() => {
                    const newSelection = appUser.region_id.includes(region.id.toString())
                      ? appUser.region_id.filter((id) => id !== region.id.toString())
                      : [...appUser.region_id, region.id.toString()];
                    handleChange({
                      target: {
                        name: 'region_id',
                        value: newSelection,
                      },
                    });
                  }}
                  color={appUser.region_id.includes(region.id.toString()) ? 'primary' : 'default'}
                  variant={appUser.region_id.includes(region.id.toString()) ? 'filled' : 'outlined'}
                  sx={{
                    borderRadius: '8px',
                    '&:hover': {
                      bgcolor: appUser.region_id.includes(region.id.toString())
                        ? 'primary.dark'
                        : 'grey.200',
                      cursor: 'pointer',
                    },
                  }}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : "Update User"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => navigate("/app-users")}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EditAppUserForm;